/**
 * The shared radiation module (fieldiq-api#1334, RAD-1) — the rules every
 * radiation screen shares: who may do what, which source events to offer, the
 * words for the API's keys, and readers that tolerate either casing.
 *
 * ROLES, as the API gates them (lib/radiation/roles.ts at af2d63fa):
 *   read     admin, office_staff, supervisor, dispatcher, field_worker, logistics
 *   admin    admin, office_staff — settings, sources, leak tests, dosimetry
 *   event    admin, office_staff, supervisor, field_worker — source events
 *   closing  admin, office_staff, supervisor — recovered, abandoned in hole,
 *            disposed (decided in the handler, matched LITERALLY on the role)
 * The admin and event gates are NON-strict requireRole lists that include
 * 'admin', so a dispatcher on a tenant with dispatcher_admin_access passes
 * them; the closing check is literal, so that dispatcher does not. A field
 * worker may check a source out only to themself, and may check in or
 * transfer only a source they hold. The server is the authority; these only
 * decide which controls to draw, and every refusal it sends is shown.
 *
 * NOTHING HERE COMPUTES A FIGURE. Current activity, decay, leak-test status,
 * due days and days left are the API's; a value it did not send renders `—`.
 *
 * VOCABULARIES. The option lists on every form come from GET /radiation/catalog.
 * The API sends keys, and labels only for isotopes, so the words for the other
 * keys live here — exhaustive over the catalog at af2d63fa. A key this file has
 * no word for renders `—`, never a guess and never a neighbouring label.
 */
import type {
  RadCatalog,
  RadDecay,
  RadDose,
  RadEvent,
  RadLeakTest,
  RadMeta,
  RadSettings,
  RadSource,
  RadSourceDetail,
  RadStatus,
} from '../types/radiation';
import { formatCalendarDate, formatInstant } from './calendar-date';

/** The register's route; a source's page is `${RAD_LIST}/:id`. */
export const RAD_LIST = '/equipment/radiation';
export const RAD_DOSIMETRY = `${RAD_LIST}/dosimetry`;
export const RAD_SETTINGS = '/settings/radiation';

export const RAD_READ_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher', 'field_worker', 'logistics'] as const;
export const RAD_ADMIN_ROLES = ['admin', 'office_staff'] as const;
export const RAD_EVENT_ROLES = ['admin', 'office_staff', 'supervisor', 'field_worker'] as const;
export const RAD_EVENT_OFFICE_ROLES = ['admin', 'office_staff', 'supervisor'] as const;

const inList = (list: readonly string[], role: string | null | undefined) => !!role && list.includes(role);

export function canReadRad(role: string | null | undefined): boolean {
  return inList(RAD_READ_ROLES, role);
}

/** Settings, registering / editing a source, a leak test, dosimetry. */
export function canAdminRad(role: string | null | undefined, dispatcherElevated = false): boolean {
  if (inList(RAD_ADMIN_ROLES, role)) return true;
  return role === 'dispatcher' && dispatcherElevated;
}

/** Recording any source event at all. */
export function canRecordRadEvent(role: string | null | undefined, dispatcherElevated = false): boolean {
  if (inList(RAD_EVENT_ROLES, role)) return true;
  return role === 'dispatcher' && dispatcherElevated;
}

/** Recording a closing event — literal on the role, as the API checks it. */
export function canCloseRad(role: string | null | undefined): boolean {
  return inList(RAD_EVENT_OFFICE_ROLES, role);
}

export const RAD_CLOSING_EVENTS = ['recovered', 'abandoned_in_hole', 'disposed'] as const;
const TERMINAL = ['abandoned_in_hole', 'disposed'];
const HANDLING = ['out_of_shield', 'in_hole', 'out_of_hole', 'in_shield'];

export interface RadActor {
  role: string | null | undefined;
  userId: string | null | undefined;
  dispatcherElevated?: boolean;
}

/**
 * The events to OFFER on this source to this person. A narrowing of the
 * buttons, not a rule: the API decides under a row lock and its refusal is
 * shown as it wrote it. Ordered custody, handling, lost, closing.
 *
 *   - a closed source (abandoned in hole, disposed) offers nothing;
 *   - a lost source offers only its close-out, to the closing roles;
 *   - check out on an in-service source nobody holds; check in and transfer on
 *     one somebody holds — and for a field worker, only one THEY hold;
 *   - the handling cycle and lost in hole on an in-service source;
 *   - disposed to the closing roles, on a source nobody holds.
 */
export function offeredEvents(source: Pick<RadSource, 'status' | 'custody'>, actor: RadActor): string[] {
  const { role, userId } = actor;
  if (!canRecordRadEvent(role, actor.dispatcherElevated)) return [];
  const status = source.status;
  if (TERMINAL.includes(status)) return [];
  const closer = canCloseRad(role);
  if (status === 'lost_in_hole') return closer ? ['recovered', 'abandoned_in_hole'] : [];
  const out: string[] = [];
  const held = source.custody?.checkedOut === true;
  const field = role === 'field_worker';
  const mine = held && !!userId && source.custody.holderUserId === userId;
  if (!held && status === 'in_service') out.push('check_out');
  if (held && (!field || mine)) out.push('check_in', 'transfer');
  if (status === 'in_service') out.push(...HANDLING, 'lost_in_hole');
  if (closer && !held) out.push('disposed');
  return out;
}

// ─── words ───────────────────────────────────────────────────────────────────

export const RAD_STATUS_LABEL: Record<string, string> = {
  in_service: 'In service',
  in_storage: 'In storage',
  lost_in_hole: 'Lost in hole',
  abandoned_in_hole: 'Abandoned in hole',
  returned: 'Returned',
  disposed: 'Disposed',
};

/**
 * Five signal hues for six statuses: green in use, blue stored, red lost or
 * abandoned in the ground, slate for a source that has left the company. The
 * label tells the shared hues apart. An unknown status takes no hue.
 */
export const RAD_STATUS_FILL: Record<string, string> = {
  in_service: 'bg-fi-signal-green',
  in_storage: 'bg-fi-signal-blue',
  lost_in_hole: 'bg-fi-signal-red',
  abandoned_in_hole: 'bg-fi-signal-red',
  returned: 'bg-fi-signal-slate',
  disposed: 'bg-fi-signal-slate',
};

export const RAD_STATUS_RAIL: Record<string, string> = {
  in_service: 'border-l-fi-signal-green',
  in_storage: 'border-l-fi-signal-blue',
  lost_in_hole: 'border-l-fi-signal-red',
  abandoned_in_hole: 'border-l-fi-signal-red',
  returned: 'border-l-fi-signal-slate',
  disposed: 'border-l-fi-signal-slate',
};

/** The API's leak-test states (lib/radiation/state.ts LEAK_STATUSES). */
export const RAD_LEAK_LABEL: Record<string, string> = {
  valid: 'Current',
  due_soon: 'Due soon',
  expired: 'Overdue',
  failed: 'Failed',
  none: 'Never tested',
  not_held: 'Not held',
};

export const RAD_LEAK_FILL: Record<string, string> = {
  valid: 'bg-fi-signal-green',
  due_soon: 'bg-fi-signal-amber',
  expired: 'bg-fi-signal-red',
  failed: 'bg-fi-signal-red',
  none: 'bg-fi-signal-slate',
  not_held: 'bg-fi-signal-slate',
};

export const RAD_EVENT_LABEL: Record<string, string> = {
  check_out: 'Check out',
  check_in: 'Check in',
  transfer: 'Transfer',
  out_of_shield: 'Out of shield',
  in_hole: 'In hole',
  out_of_hole: 'Out of hole',
  in_shield: 'Back in shield',
  lost_in_hole: 'Lost in hole',
  recovered: 'Recovered',
  abandoned_in_hole: 'Abandoned in hole',
  disposed: 'Disposed',
};

export const RAD_FORM_LABEL: Record<string, string> = {
  sealed_point: 'Sealed point source',
  neutron: 'Neutron source',
  check_source: 'Check source',
  other: 'Other',
};

export const RAD_DOSIMETER_LABEL: Record<string, string> = {
  badge: 'Film / badge',
  tld: 'TLD',
  osl: 'OSL',
  drd: 'Direct-reading (DRD)',
  epd: 'Electronic (EPD)',
};

export const RAD_RESULT_LABEL: Record<string, string> = { pass: 'Pass', fail: 'Fail' };

/** A key's word, or `—` when this file has none for it. */
export const radLabel = (map: Record<string, string>, key: string | null | undefined): string =>
  (key && map[key]) || '—';

/** The isotope's label from the catalog, else the source's own, else `—`. */
export function isotopeLabel(catalog: RadCatalog | null | undefined, key: string | null | undefined, own?: string | null): string {
  if (!key) return '—';
  return catalog?.isotopes.find((i) => i.key === key)?.label ?? own ?? '—';
}

// ─── figures (formatting only) ───────────────────────────────────────────────

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** "1.2345 GBq", or `—`. A real 0 stays 0. */
export function radQty(value: number | null | undefined, unit: string | null | undefined): string {
  if (!isNum(value)) return '—';
  const n = value.toLocaleString('en-US', { maximumFractionDigits: 4 });
  return unit ? `${n} ${unit}` : n;
}

/** A calendar day the API sent, or `—`. */
export const radDay = (value: string | null | undefined): string => formatCalendarDate(value) ?? '—';

/** An instant, in the tenant's zone, or `—`. */
export const radInstant = (value: string | null | undefined, timeZone: string | null | undefined): string =>
  formatInstant(value, timeZone ?? undefined) ?? '—';

/** The API's daysLeft, put into words. Null → `—`. */
export function radDaysLeft(days: number | null | undefined): string {
  if (!isNum(days)) return '—';
  if (days < 0) return `${Math.abs(days)} day${days === -1 ? '' : 's'} overdue`;
  if (days === 0) return 'Due today';
  return `${days} day${days === 1 ? '' : 's'} left`;
}

/** The API's error sentence, else a role sentence on a bare 403, else `fallback`. */
export function radErrorMessage(error: any, fallback: string): string {
  const msg = error?.response?.data?.error;
  if (typeof msg === 'string' && msg.trim()) return msg;
  if (error?.response?.status === 403) return 'Your role cannot make this change.';
  if (typeof error?.userMessage === 'string' && error.userMessage) return error.userMessage;
  return fallback;
}

// ─── readers (camelCase or snake_case) ───────────────────────────────────────

const pick = (o: any, camel: string, snake?: string) => {
  if (!o) return undefined;
  if (o[camel] !== undefined) return o[camel];
  return snake ? o[snake] : undefined;
};
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);

export function readMeta(m: any): RadMeta | null {
  if (!m || typeof m !== 'object') return null;
  const u = m.units ?? {};
  return {
    unitSystem: m.unitSystem ?? m.unit_system ?? '',
    units: {
      activity: str(u.activity) ?? '',
      dose: str(u.dose) ?? '',
      surveyRate: str(pick(u, 'surveyRate', 'survey_rate')) ?? '',
      depth: str(u.depth) ?? '',
    },
    timeZone: str(pick(m, 'timeZone', 'time_zone')),
    today: str(m.today),
    warnDays: Array.isArray(pick(m, 'warnDays', 'warn_days')) ? pick(m, 'warnDays', 'warn_days').map(Number) : [],
  };
}

export function readCatalog(c: any): RadCatalog | null {
  if (!c || typeof c !== 'object') return null;
  const list = (camel: string, snake: string): string[] => {
    const v = pick(c, camel, snake);
    return Array.isArray(v) ? v.filter((x: unknown): x is string => typeof x === 'string') : [];
  };
  const isotopes = Array.isArray(c.isotopes)
    ? c.isotopes
        .filter((i: any) => typeof i?.key === 'string')
        .map((i: any) => ({ key: i.key, label: str(i.label) ?? i.key, halfLifeDays: num(pick(i, 'halfLifeDays', 'half_life_days')), use: str(i.use) }))
    : [];
  return {
    isotopes,
    sourceForms: list('sourceForms', 'source_forms'),
    sourceStatuses: list('sourceStatuses', 'source_statuses'),
    patchableStatuses: list('patchableStatuses', 'patchable_statuses'),
    sourceEvents: list('sourceEvents', 'source_events'),
    custodyEvents: list('custodyEvents', 'custody_events'),
    handlingEvents: list('handlingEvents', 'handling_events'),
    closingEvents: list('closingEvents', 'closing_events'),
    dosimeterKinds: list('dosimeterKinds', 'dosimeter_kinds'),
    leakTestResults: list('leakTestResults', 'leak_test_results'),
    leakStatuses: list('leakStatuses', 'leak_statuses'),
  };
}

export function readSource(r: any): RadSource | null {
  if (!r || typeof r !== 'object' || typeof r.id !== 'string') return null;
  const lt = pick(r, 'leakTest', 'leak_test') ?? {};
  const cu = r.custody ?? {};
  const ha = r.handling ?? {};
  const le = pick(r, 'lastEvent', 'last_event');
  return {
    id: r.id,
    isotope: str(r.isotope) ?? '',
    isotopeLabel: str(pick(r, 'isotopeLabel', 'isotope_label')),
    halfLifeDays: num(pick(r, 'halfLifeDays', 'half_life_days')),
    sourceForm: str(pick(r, 'sourceForm', 'source_form')),
    serialNumber: str(pick(r, 'serialNumber', 'serial_number')),
    deviceEquipmentId: str(pick(r, 'deviceEquipmentId', 'device_equipment_id')),
    deviceName: str(pick(r, 'deviceName', 'device_name')),
    holderDescription: str(pick(r, 'holderDescription', 'holder_description')),
    referenceActivity: num(pick(r, 'referenceActivity', 'reference_activity')),
    referenceOn: str(pick(r, 'referenceOn', 'reference_on')),
    currentActivity: num(pick(r, 'currentActivity', 'current_activity')),
    currentActivityOn: str(pick(r, 'currentActivityOn', 'current_activity_on')),
    elapsedDays: num(pick(r, 'elapsedDays', 'elapsed_days')),
    licenceNumber: str(pick(r, 'licenceNumber', 'licence_number')),
    status: str(r.status) ?? '',
    leakTest: {
      status: str(lt.status),
      daysLeft: num(pick(lt, 'daysLeft', 'days_left')),
      dueOn: str(pick(lt, 'dueOn', 'due_on')),
      testedOn: str(pick(lt, 'testedOn', 'tested_on')),
    },
    custody: {
      checkedOut: pick(cu, 'checkedOut', 'checked_out') === true,
      holderUserId: str(pick(cu, 'holderUserId', 'holder_user_id')),
      holderName: str(pick(cu, 'holderName', 'holder_name')),
      lastEvent: str(pick(cu, 'lastEvent', 'last_event')),
      lastEventAt: str(pick(cu, 'lastEventAt', 'last_event_at')),
      location: str(cu.location),
    },
    handling: {
      outOfShield: pick(ha, 'outOfShield', 'out_of_shield') === true,
      lastEvent: str(pick(ha, 'lastEvent', 'last_event')),
      lastEventAt: str(pick(ha, 'lastEventAt', 'last_event_at')),
      hoursOut: num(pick(ha, 'hoursOut', 'hours_out')),
      outTooLong: bool(pick(ha, 'outTooLong', 'out_too_long')),
    },
    lastEvent: le && typeof le === 'object' && typeof le.event === 'string'
      ? { event: le.event, at: str(le.at), location: str(le.location), depth: num(le.depth) }
      : null,
    notes: str(r.notes),
  };
}

export function readLeakTest(r: any): RadLeakTest | null {
  if (!r || typeof r !== 'object' || typeof r.id !== 'string') return null;
  return {
    id: r.id,
    sourceId: str(pick(r, 'sourceId', 'source_id')),
    testedOn: str(pick(r, 'testedOn', 'tested_on')),
    dueOn: str(pick(r, 'dueOn', 'due_on')),
    intervalMonths: num(pick(r, 'intervalMonths', 'interval_months')),
    result: str(r.result),
    measuredBq: num(pick(r, 'measuredBq', 'measured_bq')),
    lab: str(r.lab),
    certificateNumber: str(pick(r, 'certificateNumber', 'certificate_number')),
    certificateUploadId: str(pick(r, 'certificateUploadId', 'certificate_upload_id')),
    notes: str(r.notes),
  };
}

export function readEvent(r: any): RadEvent | null {
  if (!r || typeof r !== 'object' || typeof r.id !== 'string') return null;
  return {
    id: r.id,
    event: str(r.event) ?? '',
    userId: str(pick(r, 'userId', 'user_id')),
    userName: str(pick(r, 'userName', 'user_name')),
    occurredAt: str(pick(r, 'occurredAt', 'occurred_at')),
    location: str(r.location),
    depth: num(r.depth),
    surveyReading: num(pick(r, 'surveyReading', 'survey_reading')),
    contextRef: str(pick(r, 'contextRef', 'context_ref')),
    notes: str(r.notes),
  };
}

export function readSourceDetail(r: any): RadSourceDetail | null {
  const s = readSource(r);
  if (!s) return null;
  const tests = pick(r, 'leakTests', 'leak_tests');
  return {
    ...s,
    leakTests: Array.isArray(tests) ? tests.map(readLeakTest).filter((t: RadLeakTest | null): t is RadLeakTest => t !== null) : [],
    events: Array.isArray(r.events) ? r.events.map(readEvent).filter((e: RadEvent | null): e is RadEvent => e !== null) : [],
  };
}

export function readDecay(r: any): RadDecay | null {
  if (!r || typeof r !== 'object') return null;
  return { on: str(r.on), elapsedDays: num(pick(r, 'elapsedDays', 'elapsed_days')), activity: num(r.activity), unit: str(r.unit) };
}

export function readDose(r: any): RadDose | null {
  if (!r || typeof r !== 'object' || typeof r.id !== 'string') return null;
  return {
    id: r.id,
    userId: str(pick(r, 'userId', 'user_id')),
    userName: str(pick(r, 'userName', 'user_name')),
    periodStart: str(pick(r, 'periodStart', 'period_start')),
    periodEnd: str(pick(r, 'periodEnd', 'period_end')),
    dosimeterKind: str(pick(r, 'dosimeterKind', 'dosimeter_kind')),
    dosimeterSerial: str(pick(r, 'dosimeterSerial', 'dosimeter_serial')),
    deepDose: num(pick(r, 'deepDose', 'deep_dose')),
    shallowDose: num(pick(r, 'shallowDose', 'shallow_dose')),
    exceedsInvestigationLevel: bool(pick(r, 'exceedsInvestigationLevel', 'exceeds_investigation_level')),
    notes: str(r.notes),
  };
}

export function readSettings(r: any): RadSettings | null {
  if (!r || typeof r !== 'object') return null;
  const wd = pick(r, 'warnDays', 'warn_days');
  return {
    leakTestIntervalMonths: num(pick(r, 'leakTestIntervalMonths', 'leak_test_interval_months')),
    warnDays: Array.isArray(wd) ? wd.map(Number).filter(Number.isFinite) : [],
    doseInvestigationLevel: num(pick(r, 'doseInvestigationLevel', 'dose_investigation_level')),
    outOfShieldAlertHours: num(pick(r, 'outOfShieldAlertHours', 'out_of_shield_alert_hours')),
    stored: r.stored === true,
  };
}

export function readStatus(r: any): RadStatus | null {
  if (!r || typeof r !== 'object') return null;
  const lt = pick(r, 'leakTests', 'leak_tests') ?? {};
  return {
    total: num(r.total),
    held: num(r.held),
    checkedOut: num(pick(r, 'checkedOut', 'checked_out')),
    outOfShield: num(pick(r, 'outOfShield', 'out_of_shield')),
    outOfShieldTooLong: num(pick(r, 'outOfShieldTooLong', 'out_of_shield_too_long')),
    lostInHole: num(pick(r, 'lostInHole', 'lost_in_hole')),
    leakTests: {
      valid: num(lt.valid),
      dueSoon: num(pick(lt, 'dueSoon', 'due_soon')),
      overdue: num(lt.overdue),
      failed: num(lt.failed),
      none: num(lt.none),
    },
    doseOverInvestigationLevel: num(pick(r, 'doseOverInvestigationLevel', 'dose_over_investigation_level')),
  };
}
